import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import SampleHardwareRevision from '../../../types/sample-hardware-revision/SampleHardwareRevision';
import SampleHardwareRevisionWriteDTO from '../../../types/sample-hardware-revision/SampleHardwareRevisionWriteDTO';
import Page from '../../../types/shared/Page';
import { BaseService } from './base.service';

@Injectable({
  providedIn: 'root'
})
export class SampleHardwareRevisionService {

  private RESOURCE: string = 'hardware-revision';

  constructor(private baseService: BaseService) {}

  /**
   * Fetches the sample hardware revisions from the backend.
   *
   * @param search The search query to filter the sample hardware revisions by.
   * @param page The page number to fetch.
   * @param size The number of elements to fetch per page.
   * @return The sample hardware revisions.
   */
  public async getSampleHardwareRevisions(search?: string, page: number = 0, size: number = 10): Promise<Page<SampleHardwareRevision>> {
    let params = new HttpParams().set('page', page.toString()).set('size', size.toString());
    if (search) {
      params = params.set('search', search);
    }

      return await this.baseService.get<Page<SampleHardwareRevision>>(this.RESOURCE, params);
  }

  /**
   * Fetches multiple sample hardware revisions from the backend.
   *
   * @param ids The IDs of the sample hardware revisions to fetch.
   * @return The sample hardware revisions.
   */
  public async getMultipleSampleHardwareRevisions(ids: number[]): Promise<SampleHardwareRevision[]> {
    const params = new HttpParams().set('ids', ids.join(','));

      return await this.baseService.get<SampleHardwareRevision[]>(this.RESOURCE + '/multiple', params);
  }

  /**
   * Fetches all sample hardware revisions from the backend.
   *
   * @return The sample hardware revisions.
   */
  public async getAllSampleHardwareRevisions(): Promise<SampleHardwareRevision[]> {
      return await this.baseService.get<SampleHardwareRevision[]>(this.RESOURCE + '/all');
  }

  /**
   * Fetches a sample hardware revision from the backend.
   *
   * @param id The ID of the sample hardware revision to fetch.
   * @return The sample hardware revision.
   */
  public async getSampleHardwareRevision(id: number): Promise<SampleHardwareRevision> {
      return await this.baseService.get<SampleHardwareRevision>(this.RESOURCE + '/' + id);
  }

  /**
   * Creates a sample hardware revision in the backend.
   *
   * @param sampleHardwareRevision The sample hardware revision to create.
   * @return The created sample hardware revision, including whether any physical sample reports its pcbVersion.
   */
  public async createSampleHardwareRevision(sampleHardwareRevision: SampleHardwareRevisionWriteDTO): Promise<SampleHardwareRevision> {
      return await this.baseService.post<SampleHardwareRevision>(this.RESOURCE, sampleHardwareRevision);
  }

  /**
   * Updates a sample hardware revision in the backend.
   *
   * @param sampleHardwareRevision The sample hardware revision to update.
   * @return The updated sample hardware revision, including whether any physical sample reports its pcbVersion.
   */
  public async updateSampleHardwareRevision(sampleHardwareRevision: SampleHardwareRevisionWriteDTO): Promise<SampleHardwareRevision> {
      return await this.baseService.put<SampleHardwareRevision>(
          this.RESOURCE + '/' + sampleHardwareRevision.id, sampleHardwareRevision);
  }

  /**
   * Deletes a sample hardware revision from the backend.
   *
   * @param id The ID of the sample hardware revision to delete.
   * @return The response from the request.
   */
  public async deleteSampleHardwareRevision(id: number): Promise<void> {
      return await this.baseService.delete<void>(this.RESOURCE + '/' + id);
  }
}
